import { AlertTriangle, Zap, RefreshCw, Home } from "lucide-react";
import { useKonami } from "@/context/KonamiContext";
import { useClickSound } from "@/hooks/useClickSound";
import { ThemedCard, ThemedText, ThemedButton, ThemedIcon } from "@/components/primitives";

// Error Boundary Display Component (functional component for hooks)
export function ErrorDisplay({
  error,
  errorId,
  onRetry,
}: {
  error?: Error;
  errorId?: string;
  onRetry: () => void;
}) {
  const { konamiMode } = useKonami();
  const play = useClickSound();

  const handleRetry = () => {
    play();
    onRetry();
  };

  const getErrorIcon = () => {
    if (konamiMode) {
      return <Zap className="h-full w-full animate-pulse" />;
    }
    return <AlertTriangle className="h-full w-full" />;
  };

  const getErrorTitle = () => {
    if (konamiMode) {
      return "SYSTEM BREACH DETECTED";
    }
    return "Something went wrong";
  };

  const getErrorMessage = () => {
    if (konamiMode) {
      return "The digital realm has encountered an anomaly. Initiating recovery protocols...";
    }
    return "An unexpected error occurred. Don't worry, we're on it!";
  };

  const getRetryText = () => {
    if (konamiMode) {
      return "REINITIALIZE SYSTEM";
    }
    return "Try Again";
  };

  const getHomeText = () => {
    if (konamiMode) {
      return "RETURN TO GRID";
    }
    return "Go Home";
  };

  return (
    <div
      className={`flex min-h-screen items-center justify-center p-4 font-mono transition-colors duration-300 ${
        konamiMode ? "bg-[#0A0A0A] text-[#00FFFF]" : "bg-black text-green-400"
      }`}
    >
      <ThemedCard konamiMode={konamiMode} className="w-full max-w-2xl text-center">
        <div className="space-y-8 p-6 sm:p-10">
          {/* Error Icon */}
          <div className="mx-auto h-16 w-16 sm:h-20 sm:w-20">
            <ThemedIcon konamiMode={konamiMode} size="xl">
              {getErrorIcon()}
            </ThemedIcon>
          </div>

          {/* Error Title */}
          <div className="space-y-6">
            <ThemedText
              konamiMode={konamiMode}
              variant="primary"
              size="xl"
              className="leading-tight font-bold sm:text-2xl md:text-3xl"
            >
              {getErrorTitle()}
            </ThemedText>

            {/* Error Message */}
            <ThemedText
              konamiMode={konamiMode}
              variant="secondary"
              className="mx-auto max-w-xl text-base leading-relaxed sm:text-lg"
            >
              {getErrorMessage()}
            </ThemedText>
          </div>

          {/* Error Details (Development) */}
          {process.env.NODE_ENV === "development" && error && (
            <details className="text-left">
              <summary className="mb-4 cursor-pointer text-center">
                <ThemedText
                  konamiMode={konamiMode}
                  variant="secondary"
                  size="sm"
                  className="sm:text-base"
                >
                  Technical Details {errorId && `(ID: ${errorId})`}
                </ThemedText>
              </summary>
              <pre
                className={`max-h-40 overflow-auto rounded-lg p-4 text-xs sm:text-sm ${
                  konamiMode
                    ? "border border-cyan-800 bg-[#111] text-cyan-300"
                    : "border border-green-800 bg-green-950/20 text-green-300"
                }`}
              >
                <code className="font-mono break-words whitespace-pre-wrap">
                  {error.stack || error.message}
                </code>
              </pre>
            </details>
          )}

          {/* Production Error Details */}
          {process.env.NODE_ENV === "production" && errorId && (
            <div className="rounded-lg border border-current/20 bg-current/5 p-4">
              <ThemedText
                konamiMode={konamiMode}
                variant="muted"
                size="sm"
                className="sm:text-base"
              >
                Technical Details (ID: {errorId})
              </ThemedText>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-center sm:gap-6">
            <ThemedButton
              konamiMode={konamiMode}
              onClick={handleRetry}
              className="flex items-center justify-center gap-3 px-6 py-3 text-base sm:text-lg"
              size="lg"
            >
              <ThemedIcon konamiMode={konamiMode} size="sm">
                <RefreshCw className="h-5 w-5" />
              </ThemedIcon>
              <span className="font-semibold whitespace-nowrap">{getRetryText()}</span>
            </ThemedButton>

            <ThemedButton
              konamiMode={konamiMode}
              variant="secondary"
              onClick={() => {
                play();
                window.location.href = "/";
              }}
              className="flex items-center justify-center gap-3 px-6 py-3 text-base sm:text-lg"
              size="lg"
            >
              <ThemedIcon konamiMode={konamiMode} size="sm">
                <Home className="h-5 w-5" />
              </ThemedIcon>
              <span className="font-semibold whitespace-nowrap">{getHomeText()}</span>
            </ThemedButton>
          </div>
        </div>
      </ThemedCard>
    </div>
  );
}
